import type { AudienceMode, Grade, SectionId, Severity } from './types';

/** Human labels for the audience modes, used by the UI and the exported report. */
export const AUDIENCE_LABELS: Record<AudienceMode, { label: string; blurb: string }> = {
  student: {
    label: 'Student / New grad',
    blurb: 'Little or no full-time experience yet. Projects and coursework count.',
  },
  jobSeeker: {
    label: 'Actively job hunting',
    blurb: 'You want recruiters to find you and reach out.',
  },
  professional: {
    label: 'Building my reputation',
    blurb: 'Employed and not looking, but want a profile that reflects your work.',
  },
  recruiter: {
    label: 'Recruiting / Hiring',
    blurb: 'You want candidates to understand what you hire for and contact you.',
  },
};

export const SECTION_LABELS: Record<SectionId, string> = {
  headline: 'Headline',
  about: 'About',
  experience: 'Experience',
  skills: 'Skills',
  completeness: 'Profile basics',
};

/**
 * How much each section contributes to the overall score, per audience mode.
 * Every row must sum to 100 — there is a test that enforces this.
 *
 * The reasoning behind the differences:
 * - Students have thin experience sections, so weighting experience heavily
 *   would just punish them for being early in their career. Headline, About,
 *   and profile basics are where they can actually win.
 * - Job seekers are read by recruiters skimming for evidence of impact, so
 *   experience carries the most weight.
 * - Recruiters are themselves the product: their headline and About decide
 *   whether a candidate trusts them enough to reply.
 */
export const SECTION_WEIGHTS: Record<AudienceMode, Record<SectionId, number>> = {
  student: { headline: 25, about: 25, experience: 20, skills: 15, completeness: 15 },
  jobSeeker: { headline: 25, about: 20, experience: 30, skills: 15, completeness: 10 },
  professional: { headline: 20, about: 25, experience: 30, skills: 10, completeness: 15 },
  recruiter: { headline: 30, about: 30, experience: 15, skills: 5, completeness: 20 },
};

/** Lower bound of each grade band, checked in descending order. */
export const GRADE_BANDS: readonly { readonly min: number; readonly grade: Grade }[] = [
  { min: 90, grade: 'A' },
  { min: 75, grade: 'B' },
  { min: 60, grade: 'C' },
  { min: 45, grade: 'D' },
  { min: 0, grade: 'F' },
];

/** Plain-language summary shown next to the score ring. */
export const GRADE_BLURBS: Record<Grade, string> = {
  A: 'Strong profile. Polish the details and keep it current.',
  B: 'Good foundation. A few targeted fixes will make it stand out.',
  C: 'The basics are there, but recruiters are missing your best work.',
  D: 'Several things are actively costing you opportunities.',
  F: 'Mostly empty or unfocused. The good news: quick wins everywhere.',
};

export function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

export function toGrade(score: number): Grade {
  const band = GRADE_BANDS.find((b) => score >= b.min);
  return band ? band.grade : 'F';
}

/** Sort order for severities: worst first. */
export function severityRank(severity: Severity): number {
  switch (severity) {
    case 'critical':
      return 0;
    case 'important':
      return 1;
    default:
      return 2;
  }
}

/**
 * Weighted mean of `[score, weight]` pairs, or `null` when there is nothing to
 * average. Returning `null` rather than 0 is what lets a fully-skipped section
 * drop out of the overall score instead of dragging it down.
 */
export function weightedMean(entries: readonly { score: number; weight: number }[]): number | null {
  const usable = entries.filter((e) => e.weight > 0);
  if (usable.length === 0) return null;
  const totalWeight = usable.reduce((sum, e) => sum + e.weight, 0);
  if (totalWeight <= 0) return null;
  const weighted = usable.reduce((sum, e) => sum + e.score * e.weight, 0);
  return weighted / totalWeight;
}
